export type NumericInput = number | string | null | undefined;

export interface FormatNumberItalianOptions {
  /** Numero massimo di decimali (es. 0 = interi, 2 = due decimali) */
  maximumFractionDigits?: number;
  /** Numero minimo di decimali (es. 2 per "1,20") */
  minimumFractionDigits?: number;
}

// `useGrouping: 'always'` forza il separatore delle migliaia anche sotto i 10.000
// (di default Intl.NumberFormat non raggruppa numeri a 4 cifre, es. 4229 vs 19.554).
const DEFAULT_FORMATTER = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 12,
  useGrouping: 'always',
} as unknown as Intl.NumberFormatOptions);

/**
 * Format a numeric value using Italian locale rules.
 * - Comma as decimal separator, dot as thousands separator
 * @param options.maximumFractionDigits - massimo decimali (es. 0 interi, 2 importi)
 * @param options.minimumFractionDigits - minimo decimali (opzionale)
 */
export function formatNumberItalian(
  value: NumericInput,
  options?: FormatNumberItalianOptions
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const raw = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, ''));

  if (!Number.isFinite(raw)) {
    return String(value);
  }

  const normalized = Number(raw.toFixed(12)); // avoid scientific notation

  if (options?.maximumFractionDigits !== undefined || options?.minimumFractionDigits !== undefined) {
    const formatter = new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 12,
      useGrouping: 'always',
    } as unknown as Intl.NumberFormatOptions);
    return formatter.format(normalized);
  }

  return DEFAULT_FORMATTER.format(normalized);
}

/**
 * Formatta con un dato numero di cifre significative (es. Indicatori: 0,000XXX).
 */
export function formatNumberItalianSignificant(
  value: NumericInput,
  significantDigits: number
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const raw = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, ''));

  if (!Number.isFinite(raw) || raw === 0) {
    return raw === 0 ? '0' : String(value);
  }

  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(raw))));
  const scaled = raw / magnitude;
  const rounded = Math.round(scaled * Math.pow(10, significantDigits - 1)) / Math.pow(10, significantDigits - 1);
  const normalized = rounded * magnitude;
  const decimals = Math.max(0, significantDigits - Math.floor(Math.log10(Math.abs(normalized)) + 1));
  const formatter = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(12, decimals),
    useGrouping: 'always',
  } as unknown as Intl.NumberFormatOptions);
  return formatter.format(normalized);
}

/**
 * Convert a string in Italian format (comma as decimal separator) to a number.
 * Handles both comma and dot as decimal separators for flexibility.
 */
export function parseNumberFromItalian(value: string | null | undefined): number | null {
  if (!value || value === '') {
    return null;
  }

  // Remove thousands separators (dots) and replace comma with dot
  const cleaned = String(value).replace(/\s/g, '').replace(/\./g, '').replace(',', '.');

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}
